import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, Method, AxiosError } from 'axios'
import { useCookies } from 'vue3-cookies'
import { AUTH_TOKEN_KEY, ID_TOKEN_KEY, EXPIRE_TIME_KEY, REFRESH_TOKEN_KEY } from './constants'
import env from './env'
import dayjs from 'dayjs'
import store from 'store'

const { cookies } = useCookies()
const ERROR_CODE = [403, 500]

axios.interceptors.response.use(
  (response) => Promise.resolve(response),
  (error) => {
    if (
      ERROR_CODE.includes(error?.response.status) &&
      !window.location.pathname.includes('login')
    ) {
      store.commit('SET_STATUS_CODE', error?.response.status)
      return store.commit('SET_OPEN_POPUP_TIMEOUT', true)
    }
    return Promise.reject(error)
  },
)
axios.defaults.headers.post['Content-Type'] = 'application/json'

function toQueryString(params: { [key: string]: string } = {}) {
  const keys: string[] = Object.keys(params || {})
  const segments: string[] = []
  keys.forEach((k) => {
    if (params[k]) {
      segments.push(`${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    }
  })
  return segments.join('&')
}

export interface ServiceOptions {
  namespace?: string
  scope?: string
  endpoint?: string
  axios?: AxiosRequestConfig
}

const defaultOptions: ServiceOptions = {
  namespace: undefined,
  endpoint: env('VITE_SERVICE_ENDPOINT'),
  axios: {},
}

export default class Service {
  axios: AxiosInstance

  private headers: any

  private options: ServiceOptions = { ...defaultOptions }

  private token: string

  /**
   * Creates an instance of Service.
   *
   * @memberOf Service
   */
  constructor(options?: ServiceOptions) {
    this.options = { ...defaultOptions, ...options }
    const baseURL = this.options.endpoint || env('VITE_SERVICE_ENDPOINT')
    this.axios = axios.create({
      baseURL,
      ...this.options.axios,
    })
    this.token = cookies.get(AUTH_TOKEN_KEY)
  }

  /**
   * Call a service action via REST API
   *
   * @param {any} action  name of action
   * @param {any} params  parameters to request
   * @returns  {Promise}
   *
   * @memberOf Service
   */
  public async rest<P>(
    action: string,
    params?: P,
    options = {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'post',
    },
  ) {
    const { headers } = options
    try {
      const expireTimes = dayjs(cookies.get(EXPIRE_TIME_KEY))
      const duration = expireTimes.diff(dayjs(), 'minute')
      this.token = cookies.get(AUTH_TOKEN_KEY)
      if (
        (this.token && duration <= 5) ||
        (!this.token && cookies.isKey(REFRESH_TOKEN_KEY) && cookies.isKey(ID_TOKEN_KEY))
      ) {
        await this.renewToken()
      }

      const opts: AxiosRequestConfig = {
        url: `${env('VITE_SERVICE_ENDPOINT')}${action}`,
        method: options.method as Method,
        data: JSON.stringify(params),
        headers: {
          ...(this.headers || {}),
          ...headers,
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      }
      const response = await axios.request(opts)
      if (response.data.data) {
        return response.data.data
      }
      return response.data
    } catch (err) {
      throw (err as AxiosError).response
    }
  }

  public get<P = any>(action: string, params?: P, options: any = {}) {
    const { headers = {} } = options
    let query = ''
    if (params) {
      query = toQueryString(params)
    }
    const path = query ? `${action}?${query}` : action
    return this.rest(
      path,
      {},
      {
        method: 'get',
        headers,
      },
    )
  }

  public post(action: string, params?: any, options: any = {}) {
    const { headers = {} } = options
    return this.rest(action, params, {
      method: 'post',
      headers,
    })
  }

  public put(action: string, params?: any, options?: any) {
    return this.rest(action, params, {
      method: 'put',
      baseURL: `${env('VITE_SERVICE_ENDPOINT')}`,
      ...options,
      headers: {
        Authorization: this.token ? `Bearer ${this.token}` : '',
        'Content-Type': 'application/json',
      },
    })
  }

  public delete(action: string, params?: any, options: any = {}) {
    const { headers = {} } = options
    const query = toQueryString(params)
    const path = query ? `${action}/${query}` : action
    return this.rest(
      path,
      {},
      {
        method: 'delete',
        headers,
      },
    )
  }

  public async renewToken() {
    store.commit('SET_LOADING', true)
    return axios
      .post(`${env('VITE_SERVICE_ENDPOINT')}/admin/renew-token`, {
        id_token: cookies.get(ID_TOKEN_KEY),
        refresh_token: cookies.get(REFRESH_TOKEN_KEY),
      })
      .then((res) => {
        if (res.data.data) {
          const { data } = res.data
          store.dispatch('auth/actSetToken', {
            idToken: data.id_token,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          })
          this.token = cookies.get(AUTH_TOKEN_KEY)
        }
      })
      .catch(() => store.dispatch('auth/actLogout'))
      .finally(() => store.commit('SET_LOADING', false))
  }
}
